import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, List, ListItemButton, ListItemText, MenuItem, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api';

interface TicketMessage {
  id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface Ticket {
  id: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
  messages?: TicketMessage[];
}

const STATUS_COLORS: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  open: 'info',
  in_progress: 'warning',
  resolved: 'success',
  closed: 'default',
};

const CATEGORIES = ['general', 'payments', 'cards', 'account', 'merchant', 'other'];

const SupportPage: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [reply, setReply] = useState('');

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/support/tickets');
      setTickets(res.data.data || []);
      setError('');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load your tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openTicket = async (id: string) => {
    try {
      const res = await api.get(`/support/tickets/${id}`);
      setSelected(res.data.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load the ticket.');
    }
  };

  const createTicket = async () => {
    if (subject.trim().length < 3 || message.trim().length < 5) {
      setError('Please provide a subject and a short description of the issue.');
      return;
    }
    try {
      setSubmitting(true);
      await api.post('/support/tickets', { subject: subject.trim(), category, message: message.trim() });
      setCreateOpen(false);
      setSubject('');
      setCategory('general');
      setMessage('');
      await loadTickets();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not create the ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim()) return;
    try {
      setSubmitting(true);
      await api.post(`/support/tickets/${selected.id}/messages`, { message: reply.trim() });
      setReply('');
      await openTicket(selected.id);
      await loadTickets();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not send your message.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Support</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Ticket
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : tickets.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tickets yet. If you need help with a payment or your account, open a ticket and our team will respond here.
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {tickets.map(t => (
              <ListItemButton key={t.id} onClick={() => openTicket(t.id)} divider>
                <ListItemText
                  primary={t.subject}
                  secondary={`${t.category} - updated ${new Date(t.updated_at).toLocaleString()}`}
                />
                <Chip size="small" label={t.status.replace('_', ' ')} color={STATUS_COLORS[t.status] || 'default'} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Support Ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Subject" value={subject} onChange={e => setSubject(e.target.value)}
              inputProps={{ maxLength: 200 }} fullWidth required />
            <TextField select label="Category" value={category} onChange={e => setCategory(e.target.value)} fullWidth>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField label="Describe the issue" value={message} onChange={e => setMessage(e.target.value)}
              multiline minRows={4} inputProps={{ maxLength: 5000 }} fullWidth required />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createTicket} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selected?.subject}
          <Chip size="small" sx={{ ml: 1 }} label={selected?.status.replace('_', ' ')}
            color={STATUS_COLORS[selected?.status || ''] || 'default'} />
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(selected?.messages || []).map(m => (
              <Box key={m.id} sx={{
                alignSelf: m.sender_type === 'user' ? 'flex-end' : 'flex-start',
                bgcolor: m.sender_type === 'user' ? 'primary.light' : 'grey.100',
                borderRadius: 2, px: 2, py: 1, maxWidth: '85%',
              }}>
                <Typography variant="caption" color="text.secondary">
                  {m.sender_type === 'user' ? 'You' : 'MalPay Support'} - {new Date(m.created_at).toLocaleString()}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{m.message}</Typography>
              </Box>
            ))}
          </Stack>
          {selected && selected.status !== 'closed' && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1}>
                <TextField value={reply} onChange={e => setReply(e.target.value)} fullWidth size="small"
                  placeholder="Write a reply" inputProps={{ maxLength: 5000 }} />
                <Button variant="contained" onClick={sendReply} disabled={submitting || !reply.trim()}>Send</Button>
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportPage;
